import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toCents } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Anticipo del cliente o gasto/material de la obra, con foto de recibo opcional.
 * Pensado para cargarse desde el teléfono en dos toques. */
export const MovementDialog = ({ open, onOpenChange, projectId }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState("gasto");
  const [monto, setMonto] = useState("");
  const [fecha, setFecha] = useState("");
  const [medioPago, setMedioPago] = useState("efectivo");
  const [proveedor, setProveedor] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTipo("gasto"); setMonto(""); setFecha(""); setMedioPago("efectivo");
    setProveedor(""); setDescripcion(""); setFile(null);
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append("tipo", tipo);
      form.append("monto", monto);
      if (fecha) form.append("fecha", new Date(fecha).toISOString());
      form.append("medio_pago", medioPago);
      if (proveedor) form.append("proveedor", proveedor);
      if (descripcion) form.append("descripcion", descripcion);
      if (file) form.append("file", file);
      return api.post(`/projects/${projectId}/movements`, form);
    },
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["project-detail", projectId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const blocked = toCents(monto) <= 0 || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="movement-dialog" className="max-w-md rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{t("projects.movements.add")}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[12.5px]">{t("projects.movements.type")}</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="anticipo">{t("projects.movements.advance")}</SelectItem>
                <SelectItem value="gasto">{t("projects.movements.expense")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.movements.amount")} *</Label>
            <Input data-testid="movement-amount-input" value={monto} placeholder="0.00" onChange={(e) => setMonto(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.movements.date")}</Label>
            <Input data-testid="movement-date-input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.movements.payment_method")}</Label>
            <Select value={medioPago} onValueChange={setMedioPago}>
              <SelectTrigger data-testid="movement-payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{t(`finance.payments.types.${m}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {tipo === "gasto" && (
            <div className="col-span-2">
              <Label className="text-[12.5px]">{t("projects.movements.supplier")}</Label>
              <Input data-testid="movement-supplier-input" value={proveedor} onChange={(e) => setProveedor(e.target.value)} />
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-[12.5px]">{t("projects.movements.description")}</Label>
            <Textarea data-testid="movement-description-input" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[12.5px]">{t("projects.movements.receipt")}</Label>
            <label className="mt-1 flex h-9 cursor-pointer items-center gap-2 border border-line px-3 text-[12.5px] text-ink-secondary hover:border-line-strong">
              <Camera className="h-3.5 w-3.5" />
              {file ? file.name : t("projects.movements.receipt")}
              <input type="file" accept="image/*,.heic,.heif" data-testid="movement-receipt-input" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="movement-submit-button" disabled={blocked} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
